import React, { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SpinnerLoader } from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { loadTask } from "~/store/actions/actions.tasks.jsx";
import DeleteTaskDependency from "./Dependencies/DeleteTaskDependecy.jsx";
import TaskAsListItemContainer from "./TaskAsListItemContainer.jsx";
import TaskCompleteCheckboxContainer from "./TaskCompleteCheckboxContainer.jsx";
import TaskFormSubmitContainer from "./TaskFormSubmitContainer.jsx";
import { TaskOptionsContainer, TaskOptionsMenu } from "./TaskOptions/index.jsx";

const TaskListItem = ({
  onStartEdit,
  id,
  canEdit,
  editMode,
  projectId,
  important,
  pending,
  fromRecurring,
  completed,
  cardMode,
  onCancelEdit,
  isDependency,
  taskDependencyId,
  taskListId,
  showConflict,
  isParent,
  isAssigneeHidden,
  isProjectNameVisible,
}) => {
  const dispatch = useDispatch();
  const [loadingTask, setLoadingTask] = useState(false);
  const taskListItemRef = useRef(null);
  const [search] = useSearchParams();
  const [object = "", objectId = 0] = (search.get("modal") || "-").split("-");
  const [taskHighlighted, setTaskHighlighted] = useState(() => object === "Task" && objectId === id);

  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => setTaskHighlighted(object === "Task" && objectId === id), [object, objectId, id]);

  const handleStartEdit = useCallback(() => {
    if (editMode || !canEdit) {
      return;
    }

    if (isDependency) {
      navigate(`/projects/${projectId}?modal=Task-${id}-${projectId}&edit=true`);
    } else {
      setLoadingTask(true);

      Promise.all([dispatch(loadTask(projectId, id, true))])
        .then(() => {
          setLoadingTask(false);
          onStartEdit(id);
        })
        .catch(() => setLoadingTask(false));
    }
  }, [editMode, canEdit, isDependency, navigate, projectId, id, dispatch, onStartEdit]);

  const handleDoubleClick = useCallback(
    (e) => {
      if (e.target === taskListItemRef.current) {
        handleStartEdit();
      }
    },
    [handleStartEdit]
  );

  return (
    <div
      className={classnames("task task_view_mode project_task_list", {
        from_recurring: fromRecurring,
        important,
        pending,
        completed_task: completed,
        "tw-opacity-50": loadingTask,
        task_highlighted: taskHighlighted,
        "tw-my-2 tw-p-4 tw-max-h-[550px] tw-min-h-auto tw-flex tw-items-center": editMode && !loadingTask,
        "tw-flex tw-items-center tw-pr-9 dependency_task": isDependency,
      })}
      onDoubleClick={handleDoubleClick}
      ref={taskListItemRef}
    >
      {!loadingTask && editMode ? (
        <div className="tw-self-stretch">
          <TaskFormSubmitContainer
            id={id}
            taskListId={taskListId}
            projectId={projectId}
            submitButtonLabel={t("Save")}
            onCancel={onCancelEdit}
            onSubmit={onCancelEdit}
            shouldOverflow
          />
        </div>
      ) : (
        <>
          {loadingTask ? (
            <SpinnerLoader className="tw-mr-2 tw-float-left tw-w-6 tw-h-6" />
          ) : (
            <TaskCompleteCheckboxContainer
              taskId={id}
              projectId={projectId}
              cardMode={cardMode}
              isDependency={isDependency}
              className="tw-mr-2 tw-float-left"
            />
          )}
          <TaskAsListItemContainer
            id={id}
            isDependency={isDependency}
            completed={completed}
            showConflict={showConflict}
            showTooltip
            isAssigneeHidden={isAssigneeHidden}
            isProjectNameVisible={isProjectNameVisible}
          />
          {canEdit && !completed && !cardMode && !isDependency ? (
            <TaskOptionsMenu
              className="task_panel_menu"
              onStartEdit={handleStartEdit}
              onVisibilityChange={setTaskHighlighted}
            >
              <TaskOptionsContainer
                id={id}
                projectId={projectId}
              />
            </TaskOptionsMenu>
          ) : null}
          {isDependency && (
            <DeleteTaskDependency
              taskId={taskDependencyId}
              id={id}
              isParent={isParent}
              projectId={projectId}
            />
          )}
        </>
      )}
    </div>
  );
};

TaskListItem.propTypes = {
  id: PropTypes.oneOfType([
    PropTypes.string, // string is used as placeholder ID until backend returns the real ID
    PropTypes.number,
  ]).isRequired,
  projectId: PropTypes.number.isRequired,
  taskDependencyId: PropTypes.number,
  taskListId: PropTypes.number,
  pending: PropTypes.bool,
  important: PropTypes.bool,
  fromRecurring: PropTypes.bool,
  completed: PropTypes.bool,
  cardMode: PropTypes.bool,
  editMode: PropTypes.bool,
  canEdit: PropTypes.bool,
  onStartEdit: PropTypes.func,
  onCancelEdit: PropTypes.func,
  isDependency: PropTypes.bool,
  showConflict: PropTypes.bool,
  isParent: PropTypes.bool,
  isAssigneeHidden: PropTypes.bool,
  isProjectNameVisible: PropTypes.bool,
};

TaskListItem.defaultProps = {
  taskDependencyId: 0,
  cardMode: false,
  skipAssignee: false,
  editMode: false,
  isDependency: false,
  showConflict: false,
  onStartEdit: () => {},
  onCancelEdit: () => {},
  isAssigneeHidden: false,
  isProjectNameVisible: false,
};

TaskListItem.displayName = "TaskListItem";

export default TaskListItem;
